<template>
  <div class="team-group-card">
    <div class="team-group-head" @click="$emit('toggle-expand', group.id)">
      <div class="team-avatar">
        {{ group.name.slice(0, 1) }}
      </div>
      <div class="team-info">
        <div class="team-title-row">
          <strong class="team-name">{{ group.name }}</strong>
          <span class="team-badge">{{ group.storeCount }} 家店铺</span>
        </div>
        <span class="team-id-label">团队节点 ID: {{ group.id }}</span>
      </div>
      <button type="button" class="team-toggle-btn" :aria-label="isExpanded ? '收起' : '展开'">
        <span class="toggle-icon" :class="{ 'is-expanded': isExpanded }">▾</span>
      </button>
    </div>

    <!-- 店铺网格列表 -->
    <div v-show="isExpanded" class="stores-grid">
      <div
        v-for="store in group.stores"
        :key="store.id"
        class="store-card"
        :class="{ 'is-active': currentStoreId === store.id }"
      >
        <div class="store-card-head">
          <span class="store-status-dot" :class="{ 'is-online': store.enabled !== false }" />
          <strong class="store-name" :title="store.name">{{ store.name }}</strong>
        </div>
        <div class="store-card-body">
          <span class="store-id-tag">ID: {{ store.id }}</span>
          <span class="store-dept-tag">{{ group.name }}</span>
        </div>
        <div class="store-card-foot">
          <button
            type="button"
            class="switch-btn"
            :disabled="currentStoreId === store.id"
            @click.stop="$emit('switch-store', store.id)"
          >
            {{ currentStoreId === store.id ? '当前工作店铺' : '切换至此店' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  group: {
    type: Object,
    required: true,
  },
  isExpanded: {
    type: Boolean,
    default: true,
  },
  currentStoreId: {
    type: String,
    default: '',
  },
});

defineEmits(['toggle-expand', 'switch-store']);
</script>

<style scoped>
.team-group-card {
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.team-group-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  cursor: pointer;
  background: #ffffff;
  user-select: none;
  transition: background 0.12s ease;
}

.team-group-head:hover {
  background: #f8fafc;
}

.team-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  display: grid;
  place-items: center;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
}

.team-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.team-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.team-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-dark, #0f172a);
}

.team-badge {
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  font-weight: 600;
}

.team-id-label {
  font-size: 11.5px;
  color: var(--text-muted, #94a3b8);
}

.team-toggle-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: #94a3b8;
}

.toggle-icon {
  display: inline-block;
  font-size: 16px;
  transition: transform 0.2s ease;
}

.toggle-icon.is-expanded {
  transform: rotate(180deg);
}

.stores-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  padding: 14px 18px 18px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
}

.store-card {
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.15s ease;
}

.store-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
}

.store-card.is-active {
  border-color: #c4b5fd;
  background: #f5f3ff;
}

.store-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.store-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #94a3b8;
  flex-shrink: 0;
}

.store-status-dot.is-online {
  background: #7c3aed;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
}

.store-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-dark, #0f172a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.store-card-body {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
}

.store-id-tag {
  color: var(--text-muted, #64748b);
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
}

.store-dept-tag {
  color: #3b82f6;
  background: #eff6ff;
  padding: 1px 5px;
  border-radius: 4px;
}

.store-card-foot {
  margin-top: 2px;
}

.switch-btn {
  width: 100%;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.switch-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: #f5f3ff;
}

.switch-btn:disabled {
  border-color: #c4b5fd;
  background: #ede9fe;
  color: #6d28d9;
  cursor: default;
  font-weight: 600;
}
</style>
